import { useState, useEffect } from 'react';
import { Trash2, ZoomIn, X, ImageOff, Loader2, FileText } from 'lucide-react';
import type { ActPhoto, IdPhoto } from './types';
import { ACT_PHOTO_TYPE_LABELS, ID_PHOTO_SIDE_LABELS } from './types';
import { getApiUrl } from '../../utils/api';

// ---------------------------------------------------------------------------
// Authenticated image: adds the JWT token to the request so the backend
// can verify the user before streaming the file.
// ---------------------------------------------------------------------------
function AuthImg({ src, alt, className }: { src: string; alt: string; className?: string }) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  // Fetch the image with the auth header on first render
  useEffect(() => {
    let revoked = false;
    let blobUrl: string | null = null;
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(getApiUrl(src), {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          cache: 'no-store',
        });
        if (!res.ok) throw new Error('not ok');
        const blob = await res.blob();
        if (!revoked) {
          blobUrl = URL.createObjectURL(blob);
          setObjectUrl(blobUrl);
        }
      } catch {
        if (!revoked) setFailed(true);
      } finally {
        if (!revoked) setLoading(false);
      }
    };
    load();
    return () => {
      revoked = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [src]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${className}`}>
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }
  if (failed || !objectUrl) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${className}`}>
        <ImageOff className="w-5 h-5 text-gray-400" />
      </div>
    );
  }
  return <img src={objectUrl} alt={alt} className={className} />;
}

// ---------------------------------------------------------------------------
// DocumentThumbnail — inline authenticated preview for a single uploaded
// document (image or PDF), with click-to-enlarge for images.
// ---------------------------------------------------------------------------
export function DocumentThumbnail({
  src,
  label,
  mimeType,
  className = 'w-full h-40',
}: {
  src: string;
  label: string;
  mimeType?: string;
  className?: string;
}) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const isImage = !mimeType || mimeType.startsWith('image/');

  if (!isImage) {
    return (
      <a
        href={getApiUrl(src)}
        target="_blank"
        rel="noopener noreferrer"
        className={`flex flex-col items-center justify-center gap-2 rounded-lg border border-gray-200 bg-gray-50 text-gray-500 hover:bg-gray-100 transition-colors ${className}`}
      >
        <FileText className="w-8 h-8" />
        <span className="text-xs">{label}</span>
      </a>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setLightboxOpen(true)}
        className={`block rounded-lg overflow-hidden border border-gray-200 bg-gray-50 ${className}`}
      >
        <AuthImg src={src} alt={label} className="w-full h-full object-cover" />
      </button>
      {lightboxOpen && (
        <Lightbox src={src} alt={label} onClose={() => setLightboxOpen(false)} />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Lightbox
// ---------------------------------------------------------------------------
function Lightbox({ src, alt, onClose }: { src: string; alt: string; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
      >
        <X className="w-6 h-6" />
      </button>
      <div onClick={(e) => e.stopPropagation()}>
        <AuthImg src={src} alt={alt} className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PhotoCard — single thumbnail with label, zoom and delete
// ---------------------------------------------------------------------------
interface PhotoCardProps {
  label: string;
  src: string;
  onDelete?: () => void;
  isDeleting?: boolean;
}

function PhotoCard({ label, src, onDelete, isDeleting }: PhotoCardProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);

  return (
    <>
      <div className="relative group rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
        <AuthImg
          src={src}
          alt={label}
          className="w-full h-32 object-cover"
        />

        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
          <button
            type="button"
            onClick={() => setLightboxOpen(true)}
            title="Agrandir"
            className="p-1.5 rounded-full bg-white/90 text-gray-800 hover:bg-white transition-colors"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              disabled={isDeleting}
              title="Supprimer"
              className="p-1.5 rounded-full bg-red-600/90 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            </button>
          )}
        </div>

        {/* Label badge */}
        <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs px-2 py-1 truncate">
          {label}
        </div>
      </div>

      {lightboxOpen && (
        <Lightbox src={src} alt={label} onClose={() => setLightboxOpen(false)} />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// ActPhotoGallery
// ---------------------------------------------------------------------------
interface ActPhotoGalleryProps {
  photos: ActPhoto[];
  onDelete?: (photoId: string) => Promise<void>;
}

export function ActPhotoGallery({ photos, onDelete }: ActPhotoGalleryProps) {
  const [deletingId, setDeletingId] = useState<string | null>(null);

  if (photos.length === 0) {
    return (
      <p className="text-sm text-gray-400 italic">Aucune photo enregistrée.</p>
    );
  }

  const handleDelete = async (id: string) => {
    if (!onDelete) return;
    setDeletingId(id);
    try {
      await onDelete(id);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
      {photos.map((photo) => (
        <PhotoCard
          key={photo.id}
          label={ACT_PHOTO_TYPE_LABELS[photo.type]}
          src={photo.url}
          onDelete={onDelete ? () => handleDelete(photo.id) : undefined}
          isDeleting={deletingId === photo.id}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// IdPhotoGallery — two fixed slots (front / back)
// ---------------------------------------------------------------------------
interface IdPhotoGalleryProps {
  photos: IdPhoto[];
  onDelete?: (photoId: string) => Promise<void>;
}

export function IdPhotoGallery({ photos, onDelete }: IdPhotoGalleryProps) {
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDelete = async (id: string) => {
    if (!onDelete) return;
    setDeletingId(id);
    try {
      await onDelete(id);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="grid grid-cols-2 gap-3 max-w-sm">
      {(['front', 'back'] as const).map((side) => {
        const photo = photos.find((p) => p.side === side);
        if (!photo) {
          return (
            <div
              key={side}
              className="flex flex-col items-center justify-center h-32 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 text-gray-400 text-xs gap-1"
            >
              <ImageOff className="w-5 h-5" />
              {ID_PHOTO_SIDE_LABELS[side]} — non capturé
            </div>
          );
        }
        return (
          <PhotoCard
            key={side}
            label={ID_PHOTO_SIDE_LABELS[side]}
            src={photo.url}
            onDelete={onDelete ? () => handleDelete(photo.id) : undefined}
            isDeleting={deletingId === photo.id}
          />
        );
      })}
    </div>
  );
}
